"use server";
/**
 * Cele două server actions ale consolei. Amândouă trec prin `ui()` → `runAction`, deci nu
 * ocolesc nimic: pachet, rol, validare, tenant, audit. Nu există aici nicio cale de execuție
 * proprie — dacă ar exista, ar fi al doilea motor, exact ce interzice AGENTS.md.
 */
import { ui, type ActionResult } from "@/lib/actions/ui";
import { consoleActions } from "@/lib/action-console";
import { LOOKUP_FOR } from "@/lib/action-console-types";
import { getSessionUser, sessionTenant } from "@/lib/auth";

/** Execută o acțiune aleasă din consolă, în numele omului din sesiune. */
export async function runFromConsole(key: string, input: unknown): Promise<ActionResult<unknown>> {
  return ui<unknown>(key, input ?? {});
}

/**
 * Alimentează pickerele de referință.
 *
 * Cheia cerută se verifică față de `LOOKUP_FOR`, nu se acceptă orice șir: altfel un client
 * modificat ar folosi ruta asta ca să ruleze ORICE acțiune de citire cu `{query, limit}`,
 * ocolind lista filtrată pe drepturi. `runAction` ar refuza oricum ce nu i se cuvine, dar o
 * suprafață care acceptă chei arbitrare e o invitație pe care n-are rost s-o lansăm.
 */
const ALLOWED_LOOKUPS = new Set(Object.values(LOOKUP_FOR).map((l) => l.action));

export async function lookupFromConsole(action: string, query: string): Promise<Record<string, unknown>[]> {
  if (!ALLOWED_LOOKUPS.has(action)) return [];
  const r = await ui<unknown>(action, { query: query || undefined, limit: 20 }, { revalidate: false });
  return r.ok && Array.isArray(r.data) ? (r.data as Record<string, unknown>[]) : [];
}

/**
 * Indexul pentru ⌘K — doar eticheta, cheia și modulul.
 *
 * Se încarcă la PRIMA deschidere a paletei, nu odată cu fiecare pagină: `AppShell` e pe toate
 * ecranele, iar cinci sute de acțiuni în payload-ul fiecăruia ar fi fost un preț plătit de toți
 * pentru o casetă pe care o deschid câțiva. Schemele nu vin aici — paleta doar duce la consolă.
 */
export type PaletteAction = { key: string; title: string; module: string };

export async function paletteActions(): Promise<PaletteAction[]> {
  const me = await getSessionUser();
  if (!me) return [];
  const list = await sessionTenant(() => consoleActions(me.companyId, { role: me.role, perms: me.perms }));
  return list.map((a) => ({ key: a.key, title: a.title, module: a.module }));
}
